import { MiddlewareRunner } from "./MiddlewareRunner.js";
const defaultFinalHandler = ([, response,]) => {
    return response ?? new Response("Not Found", { status: 404 });
};
/**
 * A middleware runner specialized for handling fetch events,
 * allowing middleware to process and modify request-response pairs.
 */
export class FetchMiddleware {
    #finalHandler;
    #runner = new MiddlewareRunner();
    constructor(finalHandler) {
        this.#finalHandler = finalHandler ?? defaultFinalHandler;
    }
    /**
     * Add a handler that processes the request-response pair.
     */
    use(handler) {
        this.#runner.add("fetch", async (pair) => {
            const result = await handler(pair);
            if (result instanceof Response) {
                const [request] = pair;
                return [request, result];
            }
            return result ?? pair;
        });
    }
    /**
     * Add a fetch handler that only processes the request,
     * if no response already exists.
     */
    useFetchHandler(handler) {
        this.use(async ([request, response]) => {
            // If a response already exists, skip this handler.
            if (response)
                return [request, response];
            return [request, await handler(request)];
        });
    }
    /**
     * Process a request and optional response through the middleware
     * chain, returning the final request-response pair.
     */
    async run(request, response) {
        return this.#runner.chain("fetch", [request, response]);
    }
    /**
     * Process a request through the middleware chain and final handler,
     * always returning a `Response`.
     *
     * @detachable
     */
    fetch = async (request) => {
        return this.#finalHandler(await this.run(request));
    };
}
